import { useState, useEffect, useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { Table, Input, Form, Flex, Checkbox, Radio, DatePicker, TimePicker, InputNumber, Select, Button } from "antd"
import { QuestionCircleOutlined, LinkOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import TextArea from "antd/es/input/TextArea"
import TableColumnsRender from "./tableComponents/Index"
import { getFormattedRequestValues, isValidDate } from "../../../utils/requestHelper"
import { formConditionsHandler } from "../../../utils/formsHelper"
import { evaluateColumnFormula } from "./tableComponents/NumberForm"
import dayjs from "dayjs"
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, applyColumnListTranslation, resolveFieldTranslation } from "../../../utils/formTranslations"

const TableInput = (props) => {
    const { t, i18n } = useTranslation();
    const lng = props.lng || i18n.language
    // Table title + each column's label/options come from `translations[lng]`.
    // Empty column labels keep the default text; option labels still apply.
    const localizedField = resolveFieldTranslation(props, lng) || props
    const translatedLabel = lng && lng !== DEFAULT_LANGUAGE
        ? props.translations?.[lng]?.label
        : undefined
    const label = (typeof translatedLabel === "string" && translatedLabel !== "")
        ? translatedLabel
        : localizedField.label
    const { tooltip, helpText, mandatory, readOnly, id, rows = [], rowCount, previewMode, formValues = null, allowNewRow = true } = localizedField
    const columns = useMemo(() => applyColumnListTranslation(
        Array.isArray(props.columns) ? props.columns : [],
        lng && lng !== DEFAULT_LANGUAGE ? props.translations?.[lng]?.columns : undefined
    ), [props.columns, props.translations, lng])
    const [tableData, setTableData] = useState([])
    const [cellColors, setCellColors] = useState({})
    const { userInfo } = useSelector((state) => state.user)

    const sanitizeRowsForForm = (rows = []) => {
        return rows.map((row = {}) => {
            const { key, ...rest } = row
            return { ...rest }
        })
    }

    const syncRowsToForm = (rows = [], forceReplace = false) => {
        if (!props.form) {
            return
        }
        const sanitizedRows = sanitizeRowsForForm(rows)
        if (forceReplace) {
            props.form.resetFields([id])
        }
        props.form.setFieldValue(id, sanitizedRows)
        // Push the rows up to the parent's currentValues too. Table edits use
        // setFieldValue, which does not fire the form's onValuesChange, so the
        // save handlers (which read currentValues) would otherwise miss the table.
        if (typeof props.passFormValues === 'function') {
            props.passFormValues({ [id]: sanitizedRows })
        }
    }

    // Recompute all computed number columns in a row and return updated row
    const recomputeRow = useCallback((row) => {
        const computedCols = columns.filter(col => col.inputType === 'number' && col.computedNumber)
        if (computedCols.length === 0) return row
        let updated = { ...row }
        computedCols.forEach(col => {
            const result = evaluateColumnFormula(col.formula, col.formulaHtml, columns, updated)
            if (result !== null) {
                updated[col.id] = result
            }
        })
        return updated
    }, [columns])

    const disabled = previewMode && readOnly ? true : false

    // True when a cell value counts as "empty" for a required column. Handles the
    // shapes cells store: strings, arrays (link/multi-select), and the user
    // picker object ({ user, group, role }).
    const isCellEmpty = (val) => {
        if (val === undefined || val === null) return true
        if (typeof val === "string") return val.trim() === ""
        if (typeof val === "number") return false
        if (Array.isArray(val)) return val.filter((v) => v !== undefined && v !== null && v !== "").length === 0
        if (typeof val === "object") {
            if ("user" in val || "group" in val || "role" in val) {
                return [...(val.user || []), ...(val.group || []), ...(val.role || [])].filter(Boolean).length === 0
            }
            return Object.keys(val).length === 0
        }
        return !val
    }

    // Columns that are required and editable (read-only cells are excluded, and
    // the whole thing only applies while filling the form).
    const mandatoryColumns = (Array.isArray(columns) ? columns : []).filter(
        (col) => col?.mandatory && col?.id && !col?.readOnly
    )

    const rules = []
    if (mandatory) {
        rules.push({ required: true })
    }
    if (previewMode && !disabled && mandatoryColumns.length > 0) {
        rules.push({
            validator: (_, value) => {
                const currentRows = Array.isArray(value) ? value : []
                const missingColumn = mandatoryColumns.find((col) =>
                    currentRows.some((row) => isCellEmpty(row?.[col.id]))
                )
                if (missingColumn) {
                    return Promise.reject(new Error(`"${missingColumn.label || "This column"}" is required in all rows`))
                }
                return Promise.resolve()
            }
        })
    }
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> } : undefined;    // Function to render different input types
    // While filling the form, Add Row and the row delete action are hidden when
    // the table is read-only or when "Allow New Row" is off. The builder canvas
    // always keeps them, so default rows can still be authored there.
    let showAddDelete = previewMode ? !disabled && Boolean(allowNewRow) : true
    // let showAddDelete = disabled ? false : true

    useEffect(() => {
        if (Array.isArray(formValues?.[id])) {
            const columnIds = (Array.isArray(columns) ? columns : []).map((col) => col?.id).filter(Boolean)
            const normalizedRows = Array.from(formValues[id])
                .filter((row) => row && typeof row === "object")
                .filter((row) => {
                    if (columnIds.length === 0) {
                        return true
                    }
                    return columnIds.some((columnId) => Object.prototype.hasOwnProperty.call(row, columnId))
                })
            const updatedTable = normalizedRows.map((data, index) => ({ ...recomputeRow(data), key: index }))
            setTableData(updatedTable)
        }
    } , [formValues, id, columns])

    // Fill the store when it holds no rows for this field — e.g. the value was
    // dropped by a re-render — so the table isn't saved as "" on submit. Every
    // other path writes explicitly via syncRowsToForm.
    //
    // Only ever fill a missing value, never overwrite one that is already there.
    // The store is this field's source of truth and the effect above derives
    // tableData from it, so tableData still holds the previous render's rows
    // whenever the store changes from outside (switching request cycles reloads
    // the whole form). Writing it back would push stale rows up while the effect
    // above pulls the new ones down, and the two would overwrite each other on
    // every render.
    useEffect(() => {
        if (!props.form || tableData.length === 0) return
        if (Array.isArray(props.form.getFieldValue(id))) return
        props.form.setFieldValue(id, sanitizeRowsForForm(tableData))
    }, [tableData, id])

    useEffect(() => {
        if (props.conditions && tableData.length > 0) {
            const newColors = {};
            tableData.forEach((row, rowIndex) => {
                const combinedData = { ...(props.formValues || {}), ...row };
                
                const actions = formConditionsHandler(props.conditions, combinedData, { id: id }, props.formElements || []);
                
                if (actions && actions.length > 0) {
                     actions.forEach(action => {
                        
                        let color = action.value;
                        let column = action.column;

                        // Unpack persisted payload format: "#hex::columnId"
                        if (color && typeof color === 'string' && color.includes('::')) {
                            const parts = color.split('::');
                            color = parts[0];
                            if (!column) {
                                column = parts[1];
                            }
                        }

                        if (action.action === "setCellColor" && column) {
                            newColors[`${rowIndex}_${column}`] = color;
                        } else if (action.action === "setCellColor" && !column) {
                        }
                    })
                }
            })
            setCellColors(newColors)
        } else {
            setCellColors({})
        }
    }, [props.conditions, tableData, props.formValues, props.formElements])

    // While filling the form, prefill any "isRequestor" user column with the
    // current user. Keyed on tableData so it re-applies (self-heals) if rows get
    // rebuilt from formValues; guarded so it only writes when a value is missing.
    useEffect(() => {
        if (!previewMode || !userInfo?.email || tableData.length === 0) return
        const requestorCols = (Array.isArray(columns) ? columns : []).filter(
            (col) => col?.inputType === "user" && col?.isRequestor && col?.id
        )
        if (requestorCols.length === 0) return

        let changed = false
        const updated = tableData.map((row) => {
            let nextRow = row
            requestorCols.forEach((col) => {
                const val = row?.[col.id]
                const hasUser = val && typeof val === "object" && Array.isArray(val.user) && val.user.length > 0
                if (!hasUser) {
                    nextRow = { ...nextRow, [col.id]: { user: [userInfo.email], group: [], role: [] } }
                    changed = true
                }
            })
            return nextRow
        })

        if (changed) {
            setTableData(updated)
            syncRowsToForm(updated)
        }
    }, [tableData, columns, userInfo, previewMode])

    const changeData = (columnId, value, rowIndex) => {
        const updatedData = tableData.map((row, index) => {
            if (index !== rowIndex) {
                return { ...row }
            }
            // Set the changed value, then recompute any formula columns in this row
            const updatedRow = { ...row, [columnId]: value }
            return recomputeRow(updatedRow)
        })

        setTableData(updatedData)

        if (props.form) {
            syncRowsToForm(updatedData)
        }
    }

    const handleDeleteRow = (recordKey) => {
        let updatedData = tableData.filter((data) => data.key !== recordKey).map((data, index) => ({...data, key: index}))
        setTableData(updatedData)
        syncRowsToForm(updatedData, true)
    }

    // When the table has an "isRequestor" user column, a row is "owned" by the
    // user(s) recorded in that column. Only owners (or admins) may delete the row.
    const requestorColumns = (Array.isArray(columns) ? columns : []).filter(
        (col) => col?.inputType === "user" && col?.isRequestor && col?.id
    )

    const getRowUsers = (record, col) => {
        const val = record?.[col.id]
        if (val && typeof val === "object" && Array.isArray(val.user)) {
            return val.user.filter(Boolean)
        }
        if (Array.isArray(val)) {
            return val.filter(Boolean)
        }
        if (typeof val === "string" && val) {
            return [val]
        }
        return []
    }

    const canDeleteRow = (record) => {
        // Ownership only restricts deletion while filling the form.
        if (!previewMode || requestorColumns.length === 0) {
            return true
        }
        if (userInfo?.isAdmin) {
            return true
        }
        if (!userInfo?.email) {
            return false
        }
        return requestorColumns.some((col) => getRowUsers(record, col).includes(userInfo.email))
    }

    // Create columns for the table based on the configured columns
    const tableColumns = columns.map((col, index) => ({
        title: (
            <span>
                {col.label || `Column ${index + 1}`}
                {col.mandatory && !col.readOnly ? <span style={{ color: "#ff4d4f", marginLeft: 2 }}>*</span> : null}
            </span>
        ),
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        width: col.width || undefined,
        onHeaderCell: () => ({
            style: col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : undefined,
        }),
        onCell: (record, rowIndex) => {
            return {
                style: {
                    backgroundColor: cellColors[`${rowIndex}_${col.id}`],
                    ...(col.width ? { width: col.width, minWidth: col.width, maxWidth: col.width } : {})
                }
            }
        },
        render: (text, record, rowIndex) => {
            // Column is disabled if table-level readOnly is true OR column-level readOnly is true
            const columnDisabled = previewMode && (disabled || col.readOnly) ? true : false
            return <TableColumnsRender disabled={columnDisabled} tableLabel={id} values={{ ...record }} {...col} allColumns={columns} documentId={rowIndex} handleChange={changeData} form={props.form} isBuilderMode={!previewMode} cellColor={cellColors[`${rowIndex}_${col.id}`]} lng={lng} />
        }
    }));
    // if(!readOnly ) {
    if(showAddDelete) {
        tableColumns.push({
            title: t('Action'),
            dataIndex: '',
            width: 80,
            align: 'center',
            onHeaderCell: () => ({ className: 'table-action-col' }),
            onCell: () => ({ className: 'table-action-col' }),
            render: (text, record) => {
                if (!canDeleteRow(record)) {
                    return null
                }
                return <DeleteOutlined onClick={() => handleDeleteRow(record.key)} />
            }

        })
    }

    // Create initial rows based on rowCount, or legacy rows array, or default to 3 empty rows
    const numberOfRows = rowCount > 0 ? rowCount : (rows.length > 0 ? rows.length : 3);
    // const tableData = Array.from({ length: numberOfRows }, (_, rowIndex) => ({
    //     key: `row_${rowIndex + 1}`,
    //     ...columns.reduce((acc, col, colIndex) => {
    //         acc[`col_${colIndex}`] = '';
    //         return acc;
    //     }, {})
    // }));

    // If no columns are configured, show a placeholder
    if (columns.length === 0) {
        return (
            <div className="form-element">
                <Flex vertical>
                    <Form.Item name={id} label={label} tooltip={help} rules={rules} className="table-field-item">
                        <div style={{
                            border: '1px dashed #d9d9d9',
                            padding: '20px',
                            textAlign: 'center',
                            color: '#999',
                            borderRadius: '6px'
                        }}>
                            <p>📊 Table Configuration Required</p>                            <p style={{ fontSize: '14px', margin: '8px 0' }}>
                                {t('Configure table structure in the form editor:')}
                            </p>
                            <p style={{ fontSize: '12px', margin: '4px 0' }}>
                                1. Select this table field
                            </p>
                            <p style={{ fontSize: '12px', margin: '4px 0' }}>
                                2. Add columns in "Edit Details" panel on the right
                            </p>
                            <p style={{ fontSize: '12px', margin: '4px 0' }}>
                                3. Configure input types for each column
                            </p>
                        </div>
                    </Form.Item>
                </Flex>
            </div>
        );
    }

    const addRow = () => {
        let newRow = {}
        columns.forEach(col => {
            newRow[col.id] = ""
            if(col.inputType === "link") {
                newRow[col.id] = ['']
            }
        })
        let updatedData = tableData.concat({...recomputeRow(newRow)}).map((data, index) => ({...data, key: index}))
        setTableData(updatedData)
        syncRowsToForm(updatedData)
    }
    return (
        <div className="form-element">
            <Flex vertical className="" gap={16}>
                <Form.Item name={id} label={label} tooltip={help} rules={rules} preserve={false} className="table-field-item">
                    <Table className="request-table"
                        dataSource={tableData}
                        columns={tableColumns}
                        pagination={false}
                        size="small"
                        style={{ backgroundColor: '#fafafa' }}
                        scroll={{x: 750}}
                    />
                </Form.Item>
                {
                    showAddDelete ?
                        <Button
                            type="dashed"
                            onClick={() => addRow()}
                            size="small"
                            icon={<PlusOutlined />}
                            style={{width: "fit-content"}}
                        >
                            {t('Add Row')}
                        </Button>
                        : null
                }
            </Flex>
        </div>
    )
}

export default TableInput
